import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ApiBase } from '../apis/ApiBase';
import { authClient } from '../apis/client-instances';
import Logo from '../assets/images/logo.webp';
import ForgotPasswordModal from '../components/ForgotModal';
import { AccountCircleIcon, KeyIcon, LockOutlinedIcon, VisibilityIcon, VisibilityOffIcon } from '../components/icons';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  Link,
  OutlinedInput,
  Paper,
  Typography,
} from '../components/ui';
import { setUser } from '../features/profile/profileSlice';
import { alert } from '../hooks/function';
import { useWorkspaceBranding } from '../hooks/useWorkspaceBranding';
import { RootState } from '../types';
import { validateEmail } from '../utils/validation';

export default function SignInPage() {
  const dispatch = useDispatch();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [openForgot, setOpenForgot] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { fetchAndApplyWorkspaceBranding, resetToAccountBranding } = useWorkspaceBranding();

  // Get account data from Redux
  const accountData = useSelector((state: RootState) => state.account);
  const accountName = accountData.name;
  const accountLogo = accountData.logoUrl;

  // Update page title when account data is loaded
  useEffect(() => {
    if (accountName) {
      document.title = `${accountName} - Sign In`;
    }
  }, [accountName]);

  // Check for existing session on mount
  useEffect(() => {
    const checkSession = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          await authClient.checkSession();
          navigate('/courses');
        }
      } catch (err) {
        console.error('Session check failed:', err);
      }
    };

    checkSession();
  }, [navigate]);

  const handleSubmit = async (event) => {
    if (!email || !password) {
      alert(enqueueSnackbar, 'Please enter email and password', 'error');
      return;
    }

    const emailValidation = validateEmail(email);
    if (!emailValidation.isValid) {
      alert(enqueueSnackbar, emailValidation.error, 'error');
      return;
    }

    try {
      setIsLoading(true);
      event.preventDefault();
      const result = await authClient.login({ email, password });
      if (result.token) {
        localStorage.setItem('token', result.token);
      }
      dispatch(setUser(result));

      // Reset to canonical account branding first so workspace branding (if any)
      // merges over fresh account data, not stale data from a previous session.
      // If the new user has no workspace, the account branding stays applied.
      const freshAccount = (await resetToAccountBranding()) ?? accountData;

      // Fetch workspace branding BEFORE navigation to prevent account branding flash
      await fetchAndApplyWorkspaceBranding(freshAccount);

      navigate('/courses');
      console.log(result);
      setIsLoading(false);
    } catch (error: any) {
      alert(enqueueSnackbar, ApiBase.getCleanErrorMessage(error, 'Login failed'), 'error');
      setIsLoading(false);
    }
  };

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleMouseUpPassword = (event) => {
    event.preventDefault();
  };

  return (
    <Container
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        minWidth: '100vw',
        padding: '5%',
        backgroundColor: 'rgb(255, 247, 240)',
      }}
      disableGutters
    >
      {/* <Paper elevation={3} sx={{ height: '100%', width: '100%', borderRadius: 3 }}> */}
      {/* <Grid container spacing={0} padding={0} sx={{ height: '100%' }}>
                    <Grid size={6} sx={{ backgroundImage: `url(${BG})`, borderRadius: 3, backgroundSize: 'cover', display: 'flex', flexDirection: 'column', justifyContent: 'center', color: 'white' }}>
                        <h1>Welcome</h1>
                    </Grid> */}
      {/* <Grid size={6} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', backgroundColor: 'transparent' }}> */}
      <Paper
        sx={{
          padding: 10,
          borderRadius: 5,
          backgroundColor: 'white',
          boxShadow: 'none',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Box
            component="img"
            src={accountLogo || Logo}
            alt={accountName || 'LMS'}
            sx={{
              width: '60%',
              objectFit: 'contain',
              marginBottom: 2,
            }}
            padding={2}
          />
          <Avatar sx={{ m: 1, bgcolor: 'black', width: 70, height: 70 }}>
            <LockOutlinedIcon sx={{ width: 40, height: 40 }} />
          </Avatar>
          <Typography component="h1" variant="h4">
            Sign In
          </Typography>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 10, width: 450, gap: 2, flexDirection: 'column', display: 'flex' }}>
            <FormControl sx={{ width: 'auto' }} variant="outlined">
              <InputLabel htmlFor="outlined-adornment-password">Email</InputLabel>
              <OutlinedInput
                id="outlined-adornment-password"
                startAdornment={
                  <InputAdornment position="start">
                    <AccountCircleIcon sx={{ color: 'action.active', my: 1.5, width: 30, height: 30 }} />
                  </InputAdornment>
                }
                label="Email"
                sx={{ borderRadius: 100 }}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </FormControl>
            <FormControl sx={{ width: 'auto', mt: 3 }} variant="outlined">
              <InputLabel htmlFor="outlined-adornment-password">Password</InputLabel>
              <OutlinedInput
                id="outlined-adornment-password"
                type={showPassword ? 'text' : 'password'}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={showPassword ? 'hide the password' : 'display the password'}
                      onClick={handleClickShowPassword}
                      onMouseDown={handleMouseDownPassword}
                      onMouseUp={handleMouseUpPassword}
                      edge="end"
                      style={{ marginRight: 1 }}
                    >
                      {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                }
                startAdornment={
                  <InputAdornment position="start">
                    <KeyIcon sx={{ color: 'action.active', my: 1.5, width: 30, height: 30 }} />
                  </InputAdornment>
                }
                label="Password"
                sx={{ borderRadius: 100 }}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSubmit(e);
                  }
                }}
              />
            </FormControl>
            <Grid container justifyContent="flex-end">
              <Grid item>
                <Link
                  onClick={() => setOpenForgot(true)}
                  variant="body2"
                  sx={{
                    color: 'black',
                    textDecorationColor: 'black', // underline color
                    cursor: 'pointer',
                  }}
                >
                  Forgot password?
                </Link>
              </Grid>
            </Grid>
            <Button fullWidth variant="contained" color="primary" disabled={isLoading} onClick={handleSubmit}>
              {isLoading ? <CircularProgress size={20} color="inherit" /> : 'Sign In'}
            </Button>
          </Box>
        </Box>
      </Paper>
      {/* </Grid> */}
      {/* </Grid> */}
      {/* </Paper> */}

      <ForgotPasswordModal open={openForgot} onClose={() => setOpenForgot(false)} />
    </Container>
  );
}
